"use client";

import Image from "next/image";
import {
  motion,
  useReducedMotion,
  useInView,
  useScroll,
  useTransform,
} from "framer-motion";
import { useRef } from "react";
import PartnerCarousel from "@/components/carousel/partnerCarousel";
import Button from "@/components/common/button";
import BlurFadeText from "@/components/common/blurFadeText";
import { partnerLogos } from "@/lib/partnerLogos";

const EASE_OUT = [0.22, 1, 0.36, 1];

export default function HeroSection() {
  const shouldReduceMotion = useReducedMotion();
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({
    target: heroRef,
    offset: ["start start", "end start"],
  });
  const heroImageY = useTransform(scrollYProgress, [0, 1], [0, 168]);
  const heroImageRef = useRef(null);
  const heroImageInView = useInView(heroImageRef, {
    once: true,
    amount: 0.22,
  });

  return (
    <section
      ref={heroRef}
      className="relative flex min-h-160 w-full flex-col overflow-hidden rounded-lg sm:min-h-180 md:min-h-195 xl:h-[1020px]"
    >
      <div className="bg-linear-to-b pointer-events-none absolute inset-x-0 top-0 z-20 flex h-48 items-end justify-center from-accent/60 via-accent/40 to-transparent px-4 pb-12 pt-16 sm:h-72 sm:px-6 sm:pb-16 sm:pt-20 xl:h-120 xl:pb-24" />
      {}
      {shouldReduceMotion ? (
        <div className="pointer-events-none absolute inset-0 z-100">
          <Image
            src="/images/background/sml-hero-image4.webp"
            alt="Hero Image"
            fill
            sizes="(max-width: 2000px) 100vw, 2000px"
            className="object-cover max-w-[2000px] lg:pr-12 mx-auto object-top brightness-100 hidden sm:block"
            priority
            unoptimized
          />
        </div>
      ) : (
        <motion.div
          ref={heroImageRef}
          initial={{ opacity: 0, x: 24, filter: "blur(10px)" }}
          animate={
            heroImageInView
              ? { opacity: 1, x: 0, filter: "blur(0px)" }
              : { opacity: 0, x: 24, filter: "blur(10px)" }
          }
          transition={{ duration: 0.78, delay: 0.08, ease: EASE_OUT }}
          style={{ y: heroImageY }}
          className="pointer-events-none absolute inset-0 z-100"
        >
          <Image
            src="/images/background/sml-hero-image4.webp"
            alt="Hero Image"
            fill
            sizes="(max-width: 2000px) 100vw, 2000px"
            className="object-cover max-w-[2000px] lg:pr-12 mx-auto object-top brightness-100 hidden sm:block"
            priority
            unoptimized
          />
        </motion.div>
      )}
      <Image
        src="/images/background/sml-hero-image3.webp"
        alt="Hero Image"
        fill
        sizes="100vw"
        className="object-cover object-bottom brightness-100"
        priority
        unoptimized
      />
      {}
      <div className="bg-linear-to-br pointer-events-none absolute left-0 top-0 z-20 h-40 w-40 from-neutral-200/50 via-transparent to-transparent sm:h-64 sm:w-64 xl:h-240 xl:w-240" />
      {}
      <div className="relative z-100 mx-auto flex w-full flex-1 px-5 xl:pb-10 pt-32 sm:pt-16 sm:px-6  sm:pt-20 xl:absolute xl:inset-x-0 xl:top-12 xl:px-48 xl:pb-0 xl:pt-0">
        <div className="mx-auto flex w-full max-w-328 items-start">
          <div className="relative z-0 flex w-full max-w-3xl sm:mt-40 flex-col items-center justify-center gap-4 font-raleway font-medium text-white sm:items-start xl:max-w-176 xl:w-3/5">
            <div className="pointer-events-none absolute left-1/2 top-1/2 -z-10 h-120 w-120 -translate-x-1/2 -translate-y-[46%] rounded-full bg-[radial-gradient(circle,rgba(0,0,0,0.36)_0%,rgba(0,0,0,0.22)_38%,rgba(0,0,0,0.1)_58%,transparent_78%)] blur-3xl sm:h-152 sm:w-152 xl:h-176 xl:w-176" />
            {}
            <h1 className="mt-0 w-full max-w-[11ch] text-center font-raleway text-[3.6rem] font-regular leading-[0.82] tracking-[-0.04em] text-white sm:max-w-[13ch] sm:text-start sm:text-[4.6rem] md:text-[5rem] lg:text-[6rem] xl:min-w-[900px] xl:text-[6.5rem]">
              <BlurFadeText
                text="The UK’s leading health and safety consultancy"
                delay={0.08}
                blur={12}
                y={24}
              />
            </h1>
            <p className="mt-1 w-full max-w-md lg:max-w-xl text-center font-raleway text-sm leading-6 font-light sm:mt-2 sm:text-start sm:text-base sm:leading-7 lg:mt-4 lg:text-lg xl:w-3/4">
              <BlurFadeText
                text="We support organisations across the UK and internationally with expert health and safety advice, audits and ongoing support."
                delay={0.18}
                blur={8}
                y={16}
              />
            </p>
            {}
            <div className="mt-5 flex w-full flex-col gap-3 sm:mt-6 sm:w-auto sm:flex-row sm:flex-wrap sm:gap-4 xl:gap-6">
              <Button
                text="Get in Touch"
                variant="primary"
                href="/contact#contact-form"
                className="w-full justify-center sm:w-auto"
              />
              <Button
                text="Explore Our Services"
                variant="border"
                href="/services"
                className="w-full justify-center sm:w-auto"
              />
            </div>
            <div className="mt-14 w-full max-w-[300px] lg:max-w-[500px] sm:mt-26 xl:mt-28">
              <p className="md:mb-12 text-center text-sm font-light tracking-[0.10em] text-white sm:text-start sm:text-[1.1rem]">
                Trusted by leading brands & businesses worldwide
              </p>
            </div>
          </div>
        </div>
      </div>
      {}
      <div className="bg-linear-to-t relative z-30 mt-auto from-accent/95 via-accent/85 to-transparent pb-8  xl:pt-24  md:pb-12  xl:absolute xl:inset-x-0 xl:bottom-0 xl:flex xl:h-120 xl:items-end xl:justify-center xl:pb-20 ">
        <PartnerCarousel logos={partnerLogos} speed={30} gap={64} />
      </div>
    </section>
  );
}
